'use client'

import { useProductBrand } from '@/providers/product-brand-provider'
import { CONNECT_BRAND } from '@/lib/connect/brand'
import { AuthShowcase } from '@/components/auth/auth-showcase'
import { cn } from '@/lib/utils'

// Shared auth chrome: a white split layout — form column on the left, a
// rounded luminist showcase card on the right (hidden below lg). Used by
// /auth, /reset-password and the choose-organization session task so all
// keep a single source of truth for the sign-in surface.
export function AuthShell({
  children,
  topAnchored = false,
  showcase,
}: {
  children: React.ReactNode
  /**
   * Keep the compact auth form's initial vertical position fixed while
   * optional fields expand below it. Reset-password screens remain centered.
   */
  topAnchored?: boolean
  /**
   * Replace the rotating Soku showcase with one static card.
   *
   * Two callers pass it: a co-branded signup page, which supplies the
   * partner's own copy, and the Connect consent screen, which supplies copy
   * about the decision being made. Everyone else omits it and keeps the
   * rotating carousel unchanged. A partner that configured no signup copy also
   * omits this and inherits the carousel, which reads as generic rather than
   * as broken.
   */
  showcase?: {
    title: string
    description: string
    imageUrl: string | null
  }
}) {
  const brand = useProductBrand()
  const effectiveShowcase = showcase ?? (brand.product === 'toolfare' ? { title: brand.name, description: CONNECT_BRAND.tagline, imageUrl: null } : undefined)
  return (
    <div className="bg-background grid min-h-screen lg:grid-cols-2">
      {/* Form column. Keep enough breathing room on mobile without making the
          credential inputs feel cramped. */}
      <div
        className={cn(
          'flex justify-center px-4 sm:px-8',
          topAnchored
            ? 'items-start pt-[max(2.5rem,calc(50vh-15.375rem))] pb-10'
            : 'items-center py-10',
        )}
      >
        <div className="w-full max-w-[460px]">{children}</div>
      </div>

      {/* Showcase column */}
      <aside className="hidden p-4 lg:block">
        <AuthShowcase override={effectiveShowcase} />
      </aside>
    </div>
  )
}
